import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Noticia, NoticiaExterna } from '../../lib/supabase';

type NoticiaUnificada = (Noticia & { tipo: 'interna' }) | (NoticiaExterna & { tipo: 'externa' });

export default function Home() {
  const navigate = useNavigate();
  const [noticias, setNoticias] = useState<NoticiaUnificada[]>([]);
  const [loadingNoticias, setLoadingNoticias] = useState(true);

  useEffect(() => {
    // Update page title and meta description for SEO
    document.title = 'N&N Soluções Empresariais - Contabilidade e Finanças Profissional';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Descomplique a contabilidade da sua empresa com a N&N Soluções Empresariais. Serviços de contabilidade, BPO financeiro e consultoria para pessoas físicas e jurídicas em Goiânia.');
    }

    // Add structured data for homepage
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "N&N Soluções Empresariais",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
      "description": "Empresa especializada em contabilidade, BPO financeiro e consultoria empresarial",
      "telephone": "+5562993911090",
      "email": "contato@nnsolucoes.com.br",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Goiânia",
        "addressRegion": "GO",
        "addressCountry": "BR"
      },
      "founder": {
        "@type": "Person",
        "name": "Vanessa Rosa Carmo Gomes",
        "jobTitle": "CEO e Contadora"
      },
      "sameAs": [
        "https://wa.me/5562993911090"
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    carregarUltimasNoticias();

    return () => {
      // Cleanup script on unmount
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('N&N Soluções Empresariais')) {
          script.remove();
        }
      });
    };
  }, []);

  const carregarUltimasNoticias = async () => {
    try {
      // Carregar notícias internas
      const { data: noticiasInternas, error: erroInternas } = await supabase
        .from('noticias')
        .select('*')
        .eq('ativo', true)
        .order('data_publicacao', { ascending: false })
        .limit(3);

      if (erroInternas) throw erroInternas;

      // Carregar notícias externas
      const { data: noticiasExternas, error: erroExternas } = await supabase
        .from('noticias_externas')
        .select('*')
        .eq('ativo', true)
        .order('data_publicacao', { ascending: false })
        .limit(3);

      if (erroExternas) throw erroExternas;

      // Unificar e ordenar por data, pegar apenas as 3 mais recentes
      const todasNoticias: NoticiaUnificada[] = [
        ...(noticiasInternas || []).map(n => ({ ...n, tipo: 'interna' as const })),
        ...(noticiasExternas || []).map(n => ({ ...n, tipo: 'externa' as const }))
      ]
        .sort((a, b) => new Date(b.data_publicacao).getTime() - new Date(a.data_publicacao).getTime())
        .slice(0, 3);

      setNoticias(todasNoticias);
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
    } finally {
      setLoadingNoticias(false);
    }
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleClickNoticia = (noticia: NoticiaUnificada) => {
    if (noticia.tipo === 'externa') {
      navigate(`/noticias/rss-${noticia.id}`);
    } else {
      navigate(`/noticias/${(noticia as Noticia).slug}`);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="https://nensolucoes.com.br/nenlogo-escura.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => navigate('/servicos')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Serviços</button>
              <button onClick={() => navigate('/sobre')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Sobre</button>
              <button onClick={() => navigate('/noticias')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Notícias</button>
              <button onClick={() => navigate('/contato')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Contato</button>
              <button onClick={() => navigate('/agendamento')} className="text-gray-700 transition-colors cursor-pointer" style={{ color: '#72142B' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>Agendamento</button>
            </nav>
            <button 
              onClick={() => navigate('/contato')}
              className="text-white px-6 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#72142B' }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Fale Conosco
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20elegant%20office%20environment%20with%20sophisticated%20businesswoman%20working%20confidently%2C%20warm%20golden%20lighting%2C%20minimalist%20workspace%20with%20accounting%20elements%2C%20professional%20atmosphere%20with%20soft%20burgundy%20and%20cream%20tones%2C%20clean%20contemporary%20design%20conveying%20trust%20and%20financial%20expertise%2C%20left%20side%20has%20subtle%20gradient%20overlay%20for%20text%20placement&width=1920&height=1080&seq=hero-modern&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#72142B]/80 via-[#72142B]/40 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-screen py-24 lg:py-0">
            <div className="text-left text-white">
              <div className="mb-6">
                <span className="inline-block px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm" style={{ backgroundColor: '#72142B', color: '#FAF7F2' }}>
                  ✨ Sua parceira em contabilidade
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Descomplique sua
                <span className="block" style={{ color: '#FAEBEA' }}>Contabilidade</span>
              </h1>
              <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed">
                Invista sua energia no que realmente importa. Deixe a gestão financeira conosco.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={() => navigate('/contato')}
                  className="text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  style={{ backgroundColor: '#72142B' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Começar agora
                </button>
                <button 
                  onClick={() => navigate('/servicos')}
                  className="border-2 text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold backdrop-blur-sm"
                  style={{ borderColor: '#FAEBEA', color: '#FAEBEA' }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#FAEBEA';
                    e.target.style.color = '#72142B';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#FAEBEA';
                  }}
                >
                  Nossos serviços
                </button>
              </div>
              <div className="grid grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold" style={{ color: '#FAEBEA' }}>5+</div>
                  <div className="text-sm text-gray-300">Anos de experiência</div>
                </div>
                <div>
                  <div className="text-3xl font-bold" style={{ color: '#FAEBEA' }}>220+</div>
                  <div className="text-sm text-gray-300">Clientes atendidos</div>
                </div>
                <div>
                  <div className="text-3xl font-bold" style={{ color: '#FAEBEA' }}>24h</div>
                  <div className="text-sm text-gray-300">Suporte disponível</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Serviços */}
      <section className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
              Nossos Serviços
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
              Soluções completas para seu negócio
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Oferecemos uma gama completa de serviços contábeis e financeiros para pessoas físicas e jurídicas
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2" style={{ borderColor: '#FAEBEA' }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-calculator-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>Contabilidade Completa</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Gestão contábil completa com foco em reduzir seu estresse e garantir as melhores opções tributárias.
              </p>
              <button 
                onClick={() => navigate('/servicos')}
                className="font-semibold transition-colors cursor-pointer flex items-center group"
                style={{ color: '#72142B' }}
                onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Saiba mais 
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>
            <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2" style={{ borderColor: '#FAEBEA' }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>BPO Financeiro</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Organização completa de documentos financeiros com metodologia especializada e atualizações regulares.
              </p>
              <button 
                onClick={() => navigate('/servicos')}
                className="font-semibold transition-colors cursor-pointer flex items-center group"
                style={{ color: '#72142B' }}
                onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Saiba mais 
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>
            <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2" style={{ borderColor: '#FAEBEA' }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#72142B' }}>
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#72142B' }}>Consultoria Empresarial</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Abertura de empresas e consultoria societária com profissionais experientes e atendimento personalizado.
              </p>
              <button 
                onClick={() => navigate('/servicos')}
                className="font-semibold transition-colors cursor-pointer flex items-center group"
                style={{ color: '#72142B' }}
                onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Saiba mais 
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>
          </div>
          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/servicos')}
              className="text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              style={{ backgroundColor: '#72142B' }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Ver todos os serviços
            </button>
          </div>
        </div>
      </section>

      {/* Por que escolher a N&N */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
              Por que nos escolher
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
              Diferenciais que fazem a diferença
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#FAEBEA' }}>
                <i className="ri-shield-check-line text-3xl" style={{ color: '#72142B' }}></i>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#72142B' }}>Segurança Total</h3>
              <p className="text-gray-600">Proteção completa dos seus dados e informações financeiras</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#FAEBEA' }}>
                <i className="ri-time-line text-3xl" style={{ color: '#72142B' }}></i>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#72142B' }}>Agilidade</h3>
              <p className="text-gray-600">Processos otimizados para entregas rápidas e eficientes</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#FAEBEA' }}>
                <i className="ri-user-heart-line text-3xl" style={{ color: '#72142B' }}></i>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#72142B' }}>Atendimento Personalizado</h3>
              <p className="text-gray-600">Cada cliente é único e merece atenção especializada</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#FAEBEA' }}>
                <i className="ri-award-line text-3xl" style={{ color: '#72142B' }}></i>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#72142B' }}>Excelência</h3>
              <p className="text-gray-600">Compromisso com a qualidade em todos os nossos serviços</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre a CEO */}
      <section className="py-20" style={{ backgroundColor: '#FAEBEA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block bg-white px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ color: '#72142B' }}>
                Conheça nossa CEO
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
                Vanessa Rosa Carmo Gomes
              </h2>
              <p className="text-xl mb-6 leading-relaxed" style={{ color: '#72142B' }}>
                Contadora formada pela Universidade Estácio de Sá, apaixonada por Finanças e dedicada a descomplicar a contabilidade para empresários.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                "Meu objetivo é levar praticidade e facilidade às empresas, trabalhando com dignidade, honestidade e eficiência em tudo que fazemos."
              </p>
              <button 
                onClick={() => navigate('/sobre')}
                className="text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                style={{ backgroundColor: '#72142B' }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Conheça nossa história
              </button>
            </div>
            <div className="order-1 lg:order-2 text-center">
              <div className="relative inline-block">
                <img
                  src="https://static.readdy.ai/image/3e6f8fcc8f2630e876a6bece5f75fd5d/8bde811c43268917f91f37ea02ced34e.jpeg"
                  alt="Vanessa Rosa Carmo Gomes - CEO"
                  className="w-80 h-96 object-cover rounded-2xl shadow-2xl mx-auto"
                />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: '#72142B' }}>
                  <i className="ri-award-line text-3xl text-white"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Últimas Notícias */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
              Blog
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#72142B' }}>
              Últimas Notícias
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fique por dentro das novidades sobre contabilidade e gestão empresarial
            </p>
          </div>

          {loadingNoticias ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: '#FAEBEA', borderTopColor: '#72142B' }}></div>
            </div>
          ) : noticias.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {noticias.map((noticia) => (
                  <article 
                    key={`${noticia.tipo}-${noticia.id}`} 
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border cursor-pointer" 
                    style={{ borderColor: '#FAEBEA' }} 
                    onClick={() => handleClickNoticia(noticia)}
                  >
                    {noticia.imagem_url && (
                      <div className="w-full h-48 overflow-hidden">
                        <img
                          src={noticia.imagem_url}
                          alt={noticia.titulo}
                          className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3 text-sm">
                        <span className="flex items-center text-gray-500">
                          <i className="ri-calendar-line mr-1"></i>
                          {formatarData(noticia.data_publicacao)}
                        </span>
                        {noticia.tipo === 'externa' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#FAEBEA', color: '#72142B' }}>
                            <i className="ri-external-link-line mr-1"></i>
                            {(noticia as NoticiaExterna).fonte}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-3" style={{ color: '#72142B' }}>
                        {noticia.titulo}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {noticia.resumo}
                      </p>
                      <span className="font-semibold transition-colors inline-flex items-center group" style={{ color: '#72142B' }}>
                        {noticia.tipo === 'externa' ? 'Ler no site original' : 'Ler mais'}
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                      </span>
                    </div>
                  </article>
                ))}
              </div>
              <div className="text-center mt-12">
                <button 
                  onClick={() => navigate('/noticias')}
                  className="text-white px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  style={{ backgroundColor: '#72142B' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Ver todas as notícias
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Nenhuma notícia disponível no momento</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#72142B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#FAF7F2' }}>
            Pronto para simplificar sua contabilidade?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto" style={{ color: '#FAEBEA' }}>
            Entre em contato conosco e descubra como podemos ajudar sua empresa a crescer com tranquilidade
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contato')}
              className="px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              style={{ backgroundColor: '#FAF7F2', color: '#72142B' }}
              onMouseEnter={(e) => e.target.style.opacity = '0.9'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Fale conosco agora
            </button>
            <button 
              onClick={() => navigate('/agendamento')}
              className="border-2 px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-lg font-semibold"
              style={{ borderColor: '#FAF7F2', color: '#FAF7F2' }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#FAF7F2';
                e.target.style.color = '#72142B';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#FAF7F2';
              }}
            >
              Agendar consulta
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <img 
                src="https://nensolucoes.com.br/nenlogo-cinca.png" 
                alt="N&N Soluções Empresariais" 
                className="h-16 w-auto mb-4 cursor-pointer"
                onClick={() => navigate('/')}
              />
              <p className="text-gray-300 mb-6">
                Simplificando sua contabilidade para você focar no que realmente importa.
              </p>
              <div className="space-y-2">
                <p className="flex items-center text-gray-300">
                  <i className="ri-phone-line mr-3"></i>
                  WhatsApp: (62) 99391-1090
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-mail-line mr-3"></i>
                  contato@nnsolucoes.com.br
                </p>
                <p className="flex items-center text-gray-300">
                  <i className="ri-map-pin-line mr-3"></i>
                  Av. Goiás, 606 - Setor Central, Goiânia - GO
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/servicos')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Serviços</button></li>
                <li><button onClick={() => navigate('/sobre')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Sobre</button></li>
                <li><button onClick={() => navigate('/noticias')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Notícias</button></li>
                <li><button onClick={() => navigate('/contato')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Contato</button></li>
                <li><button onClick={() => navigate('/agendamento')} className="text-gray-300 hover:text-white transition-colors cursor-pointer">Agendamento</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Nossos Serviços</h4>
              <ul className="space-y-2">
                <li><span className="text-gray-300">Contabilidade</span></li>
                <li><span className="text-gray-300">BPO Financeiro</span></li>
                <li><span className="text-gray-300">Departamento Pessoal</span></li>
                <li><span className="text-gray-300">Consultoria Financeira</span></li>
                <li><span className="text-gray-300">Declaração de Impostos</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-300">
              © 2026 N&N Soluções Empresariais. Todos os direitos reservados. | 
              <a href="https://www.instagram.com/rodriigocoelho" target="_blank" rel="noopener noreferrer" className="hover:text-gray-100 ml-1 cursor-pointer" style={{ color: '#FAEBEA' }}>
                RS DIGITAL
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
